import { Card as BaseCard, CardContent } from '../card';
import { Button, type ButtonProps } from '../button';
import { Eye, EyeClosed } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useMeasure } from '../../hooks/use-measure';

const BASE_CLASS = 'mm-flipcard';

export type FlipcardProps = React.ComponentProps<'div'> & {
  front?: React.ReactNode;
  back?: React.ReactNode;
  flip?: boolean;
  height?: string | number;
  slots?: {
    Card?: React.ElementType;
  };
  slotProps?: {
    card?: React.ComponentProps<'div'>;
  };
};

export const Flipcard = (props: FlipcardProps) => {
  const {
    front,
    back,
    flip,
    children,
    className,
    height,
    slots = {},
    slotProps,
    ...others
  } = props;

  const { Card = BaseCard } = slots;

  const [frontRef, frontDimensions] = useMeasure();
  const [backRef, backDimension] = useMeasure();

  const sideSize = {
    front: frontDimensions.height || 0,
    back: backDimension.height || 0,
  };

  // console.log(sideSize);
  const cardHeight = Math.max(sideSize.front, sideSize.back) || 'auto';

  return (
    <div
      className={cn(BASE_CLASS, 'w-full', className)}
      data-flipped={flip ? true : undefined}
      data-slot="flipcard"
      {...others}
    >
      <div
        className="relative transition-transform duration-600 transform-3d in-data-flipped:rotate-y-180"
        style={{ height: cardHeight }}
      >
        {['front', 'back'].map((side) => {
          const ref = { front: frontRef, back: backRef }[side];
          const content = { front, back }[side];

          return (
            <div
              key={side}
              className={cn(
                'absolute inset-x-0 top-0 min-h-full backface-hidden',
                { 'rotate-y-180': side === 'back' }
              )}
              data-side={side}
            >
              <Card
                {...slotProps?.card}
                ref={ref}
                data-slot={`${side}-card`}
                height={height}
                className={cn(
                  'flex-col justify-center border-2 border-card-foreground shadow-none',
                  slotProps?.card?.className
                )}
              >
                {typeof content === 'string' ? (
                  <FlipcardContent>{content}</FlipcardContent>
                ) : (
                  content
                )}
              </Card>
            </div>
          );
        })}
      </div>
      {children}
    </div>
  );
};

export const FlipcardContent = ({
  className,
  ...others
}: React.ComponentProps<'div'>) => (
  <CardContent
    data-slot="flipcard-content"
    className={cn('text-center p-8', className)}
    {...others}
  />
);

export type FlipcardButtonProps = ButtonProps & {
  flipped?: boolean;
  label?: string;
};

export const FlipcardButton = ({
  flipped,
  className,
  label,
  children,
  ...others
}: FlipcardButtonProps) => {
  const rootClassName = cn(
    // { 'flex-row-reverse': flipped },
    className
  );

  return (
    <Button className={rootClassName} data-slot="flipcard-button" {...others}>
      {!flipped ? <Eye /> : <EyeClosed />} {children || label}
    </Button>
  );
};
